import { Component, inject, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { UsersService } from '../users.service';
import { User } from '../users.type';
import { UserRole } from '../../auth/auth.type';

interface DialogData {
  user: User;
}

@Component({
  selector: 'app-manage-roles-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
  ],
  template: `
    <h2 mat-dialog-title>Manage Roles — {{ data.user.username }}</h2>

    <mat-dialog-content>
      @if (loading) {
        <div class="spinner-wrap"><mat-spinner diameter="32"></mat-spinner></div>
      } @else {
        <div class="current-roles">
          <p class="section-label">Current roles</p>
          <div class="chips-row">
            @for (role of currentRoles; track role) {
              <mat-chip class="role-chip" [removable]="isRemovable(role)" (removed)="removeRole(role)">
                {{ role }}
                @if (isRemovable(role)) {
                  <mat-icon matChipRemove>cancel</mat-icon>
                }
              </mat-chip>
            }
            @if (currentRoles.length === 0) {
              <span class="empty">No roles assigned</span>
            }
          </div>
        </div>

        <div class="assign-role">
          <p class="section-label">Assign a role</p>
          <mat-form-field appearance="outline" class="role-select">
            <mat-label>Role</mat-label>
            <mat-select [(ngModel)]="selectedRole">
              @for (role of availableRoles; track role) {
                <mat-option [value]="role">{{ role }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button mat-raised-button color="primary" [disabled]="!selectedRole || assigning"
                  (click)="assignRole()">
            {{ assigning ? 'Assigning…' : 'Assign' }}
          </button>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(rolesChanged)">Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .spinner-wrap { display: flex; justify-content: center; padding: 24px; }
    .section-label { font-weight: 600; margin-bottom: 8px; color: var(--mat-sys-on-surface-variant, #555); }
    .chips-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
    .assign-role { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
    .role-select { flex: 1; min-width: 160px; }
    .empty { color: #888; font-style: italic; }
  `]
})
export class ManageRolesDialog implements OnInit {
  dialogRef = inject(MatDialogRef<ManageRolesDialog>);
  private usersService = inject(UsersService);
  private snackBar = inject(MatSnackBar);

  loading = true;
  assigning = false;
  currentRoles: UserRole[] = [];
  allRoles: UserRole[] = [];
  selectedRole: UserRole | null = null;
  rolesChanged = false;

  /** Feature-access roles that a SuperAdmin may assign/remove */
  readonly featureRoles: UserRole[] = ['AuctionAccess'];

  constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData) {}

  ngOnInit(): void {
    this.loadData();
  }

  get availableRoles(): UserRole[] {
    return this.allRoles.filter(r => !this.currentRoles.includes(r));
  }

  /** Only feature-access roles can be removed via this dialog */
  isRemovable(role: UserRole): boolean {
    return this.featureRoles.includes(role);
  }

  private loadData(): void {
    this.loading = true;
    this.usersService.getAllRoles().subscribe({
      next: res => {
        this.allRoles = res.result;
        this.loadUserRoles();
      },
      error: () => {
        this.snackBar.open('Error loading roles', 'Close', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  private loadUserRoles(): void {
    this.usersService.getUserRoles(this.data.user.user_id).subscribe({
      next: res => {
        this.currentRoles = res.result.roles;
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Error loading user roles', 'Close', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  assignRole(): void {
    if (!this.selectedRole) return;
    this.assigning = true;
    this.usersService.assignRole(this.data.user.user_id, { role: this.selectedRole }).subscribe({
      next: () => {
        this.snackBar.open(`Role "${this.selectedRole}" assigned`, 'Close', { duration: 3000 });
        this.rolesChanged = true;
        this.selectedRole = null;
        this.loadUserRoles();
        this.assigning = false;
      },
      error: (err) => {
        const msg = err.status === 422
          ? 'A prerequisite role is missing for this role'
          : err.error?.error || 'Error assigning role';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.assigning = false;
      }
    });
  }

  removeRole(role: UserRole): void {
    this.usersService.removeRole(this.data.user.user_id, role).subscribe({
      next: () => {
        this.snackBar.open(`Role "${role}" removed`, 'Close', { duration: 3000 });
        this.rolesChanged = true;
        this.loadUserRoles();
      },
      error: (err) => {
        const msg = err.status === 422
          ? 'Cannot remove role: another assigned role depends on it'
          : err.error?.error || 'Error removing role';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }
}
